import { useCallback, useSyncExternalStore } from "react";
import type { Marketplace, MarketplaceImportRecord } from "./domain/types";
import { validImportOpeningQuantity } from "./domain/importQuantities";

export type DirectMarketplace = Extract<Marketplace, "ebay" | "etsy">;
export type ImportBulkAction = "create_new" | "ignore" | "saved";
export const MAX_IMPORT_SELECTION = 1_000;
export type ImportQuantityDrafts = Record<string, { value: string; sourceFingerprint: string }>;
export interface ImportMatchContext {
  recordID: string;
  query: string;
  cursor?: string;
  previousCursors: string[];
}
export interface ImportView {
  runID?: string;
  page: number;
  search: string;
  remainingOnly: boolean;
  selectedIDs: string[];
  bulkAction?: ImportBulkAction;
  scrollTop?: number;
  matcher?: ImportMatchContext;
  openingQuantities?: ImportQuantityDrafts;
}
export interface PendingImport {
  runID: string;
  page: number;
  recordIDs: string[];
  openingQuantities?: ImportQuantityDrafts;
}

export function importOpeningQuantityValue(record: Pick<MarketplaceImportRecord, "id" | "sourceFingerprint" | "sourceQuantityNeedsReview" | "observedSourceQuantity">, drafts?: ImportQuantityDrafts): string {
  const draft = drafts?.[record.id];
  if (draft?.sourceFingerprint === record.sourceFingerprint) return draft.value;
  return !record.sourceQuantityNeedsReview && validImportOpeningQuantity(record.observedSourceQuantity) ? String(record.observedSourceQuantity) : "";
}

export function defaultImportView(): ImportView {
  return { page: 1, search: "", remainingOnly: false, selectedIDs: [] };
}
function object(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}
function text(value: unknown, max = 256): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= max;
}
function pageNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 1 && value <= 1_000_000;
}
function ids(value: unknown): value is string[] {
  return Array.isArray(value) && value.length <= MAX_IMPORT_SELECTION && value.every((id) => text(id));
}
function quantityDrafts(value: unknown): ImportQuantityDrafts | undefined {
  if (!object(value) || Object.keys(value).length > MAX_IMPORT_SELECTION) return undefined;
  return Object.fromEntries(Object.entries(value).filter(([id, draft]) => text(id) && object(draft)
    && typeof draft.value === "string" && draft.value.length <= 32 && text(draft.sourceFingerprint, 128))) as ImportQuantityDrafts;
}
function read(key: string): unknown {
  try {
    const raw = window.localStorage.getItem(key);
    return raw && raw.length <= 512_000 ? JSON.parse(raw) : undefined;
  } catch { return undefined; }
}
function write(key: string, value: unknown) {
  try { window.localStorage.setItem(key, JSON.stringify(value)); return true; } catch { return false; }
}
export function readImportView(scope: string): ImportView {
  const saved = read(`${scope}:view`);
  if (!object(saved) || saved.version !== 1 || !object(saved.view) || typeof saved.savedAt !== "number" || Date.now() - saved.savedAt > 7 * 86_400_000) return defaultImportView();
  const value = saved.view;
  const view: ImportView = {
    runID: text(value.runID) ? value.runID : undefined,
    page: pageNumber(value.page) ? value.page : 1,
    search: typeof value.search === "string" ? value.search.slice(0, 200) : "",
    remainingOnly: value.remainingOnly === true,
    selectedIDs: ids(value.selectedIDs) ? [...new Set(value.selectedIDs)] : [],
    bulkAction: value.bulkAction === "create_new" || value.bulkAction === "ignore" || value.bulkAction === "saved" ? value.bulkAction : ids(value.selectedIDs) && value.selectedIDs.length > 0 ? "saved" : "create_new",
    scrollTop: typeof value.scrollTop === "number" && Number.isFinite(value.scrollTop) && value.scrollTop >= 0 ? value.scrollTop : 0,
    openingQuantities: quantityDrafts(value.openingQuantities)
  };
  const matcher = value.matcher;
  if (object(matcher) && text(matcher.recordID) && typeof matcher.query === "string" && Array.isArray(matcher.previousCursors)
    && matcher.previousCursors.length <= 1_000 && matcher.previousCursors.every((cursor) => typeof cursor === "string" && cursor.length <= 512)) {
    view.matcher = { recordID: matcher.recordID, query: matcher.query.slice(0, 200), cursor: text(matcher.cursor, 512) ? matcher.cursor : undefined, previousCursors: matcher.previousCursors as string[] };
  }
  return view;
}
export function writeImportView(scope: string, view: ImportView) {
  return write(`${scope}:view`, { version: 1, savedAt: Date.now(), view });
}
export function readPendingImport(scope: string): PendingImport | undefined {
  const value = read(`${scope}:pending`);
  if (!object(value) || !text(value.runID) || !pageNumber(value.page) || !ids(value.recordIDs) || value.recordIDs.length === 0) return undefined;
  return { runID: value.runID, page: value.page, recordIDs: [...new Set(value.recordIDs)], ...(value.openingQuantities ? { openingQuantities: quantityDrafts(value.openingQuantities) } : {}) };
}
/** Preserve pre-release recovery data only for the same user/workspace/seller. */
export function migratePendingImport(scope: string, legacyPrefix: string, externalAccountID?: string, environment?: string): PendingImport | undefined {
  if (!externalAccountID || !environment) return undefined;
  try {
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (!key?.startsWith(legacyPrefix)) continue;
      const parts = key.slice(legacyPrefix.length).split(":");
      if (parts.length !== 4 || parts[3] !== "pending" || parts[1] !== encodeURIComponent(externalAccountID) || parts[2] !== encodeURIComponent(environment)) continue;
      const legacyScope = key.slice(0, -":pending".length);
      const pending = readPendingImport(legacyScope);
      if (!pending) continue;
      if (writePendingImport(scope, pending)) {
        writeImportView(scope, { ...readImportView(legacyScope), runID: pending.runID, page: pending.page });
        clearPendingImport(legacyScope);
      }
      // Keep the legacy checkpoint if storage is full. Recovery remains read-only.
      return pending;
    }
  } catch { /* Normal storage warnings and write guards still apply. */ }
  return undefined;
}

export function writePendingImport(scope: string, pending: PendingImport) {
  // A view checkpoint must never erase recovery information for an uncertain write.
  if (!text(pending.runID) || !pageNumber(pending.page) || !ids(pending.recordIDs) || !pending.recordIDs.length) return false;
  return write(`${scope}:pending`, pending);
}
export function clearPendingImport(scope: string) {
  try { window.localStorage.removeItem(`${scope}:pending`); } catch { /* Recheck the same saved statuses on the next visit. */ }
}

type Activity = { token: symbol; kind: "saving" | "applying"; marketplace: DirectMarketplace };
const activities = new Map<string, Activity>();
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };
const serverSnapshot = () => undefined;
const notify = () => listeners.forEach((listener) => listener());
function publishBusy(marketplace: DirectMarketplace) {
  if (typeof window === "undefined") return;
  const busy = [...activities.values()].some((activity) => activity.marketplace === marketplace);
  window.dispatchEvent(new CustomEvent("listwork:marketplace-import-busy", { bubbles: true, detail: { marketplace, busy } }));
}
export function useImportActivity(scope: string) {
  return useSyncExternalStore(subscribe, useCallback(() => activities.get(scope)?.kind, [scope]), serverSnapshot);
}
export function beginImportActivity(scope: string, kind: Activity["kind"], marketplace: DirectMarketplace) {
  if (activities.has(scope)) return undefined;
  const token = Symbol(kind);
  activities.set(scope, { token, kind, marketplace });
  publishBusy(marketplace);
  notify();
  return token;
}
export function endImportActivity(scope: string, token: symbol) {
  const activity = activities.get(scope);
  if (activity?.token !== token) return;
  activities.delete(scope);
  publishBusy(activity.marketplace);
  notify();
}
export function importIsResolved(record: MarketplaceImportRecord) {
  return ["created", "linked", "ignored", "already_linked"].includes(record.status);
}
export function importCanApply(record: MarketplaceImportRecord) {
  return ["ready", "failed"].includes(record.status)
    && (record.identityDecision === "ignore" || (record.classification !== "cannot_import"
      && (record.identityDecision === "create_new" || (record.identityDecision === "link_existing" && Boolean(record.targetItemID)))));
}
export function importCanSelect(record: MarketplaceImportRecord) {
  return !importIsResolved(record) && record.status !== "applying";
}
/** Keep each network request inside the existing API's bounded contract. */
export function importChunks<T>(items: T[]): T[][] {
  return Array.from({ length: Math.ceil(items.length / 50) }, (_, index) => items.slice(index * 50, (index + 1) * 50));
}
export function mergeImportRecords(current: MarketplaceImportRecord[], next: MarketplaceImportRecord[]) {
  const records = new Map(current.map((record) => [record.id, record]));
  next.forEach((record) => records.set(record.id, record));
  return [...records.values()];
}
export function safeImportError(reason: unknown, fallback: string) {
  const message = reason instanceof Error ? reason.message.trim() : "";
  return message && message.length <= 500 && !/oauth|bearer|token|secret|api.?key|https?:\/\/|http \d+|sql|stack trace/i.test(message) ? message : fallback;
}
